import { Button, Card, Group, Popover, Stack } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendar, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  daysAgoISODate,
  displayDateToISODate,
  formatISODate,
  isoDateToDisplayDate,
  todayISODate,
} from '../../helpers/dateUtils';

/**
 * DateRangePicker component that works with ISO date strings (YYYY-MM-DD).
 *
 * Props:
 * - startDate: string - ISO date "YYYY-MM-DD"
 * - endDate: string - ISO date "YYYY-MM-DD"
 * - setStartDate: (date: string) => void
 * - setEndDate: (date: string) => void
 * - setDateRange: (start: string, end: string) => void (optional) - for atomic updates
 * - minDate: string (optional) - ISO date "YYYY-MM-DD"
 * - maxDate: string (optional) - ISO date "YYYY-MM-DD"
 * - preSetRanges: boolean - whether to show predefined range buttons
 * - numberOfColumns: number (optional) - calendar months shown on desktop (default 2)
 */
const DateRangePicker = ({
  preSetRanges,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  setDateRange,
  minDate,
  maxDate,
  numberOfColumns = 2,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Convert ISO date strings to Date objects for the DatePicker display
  const [customRange, setCustomRange] = useState([
    isoDateToDisplayDate(startDate),
    isoDateToDisplayDate(endDate),
  ]);

  // Sync customRange when startDate or endDate props change from outside
  useEffect(() => {
    setCustomRange([isoDateToDisplayDate(startDate), isoDateToDisplayDate(endDate)]);
  }, [startDate, endDate]);

  // Returns ISO date strings for predefined ranges
  const predefinedRange = (days) => {
    return [daysAgoISODate(days - 1), todayISODate()];
  };

  const onSetDates = ([start, end]) => {
    // Convert Date objects from picker to ISO date strings
    const startISODate = displayDateToISODate(start);
    const endISODate = displayDateToISODate(end);

    // Use atomic update if available, otherwise fall back to separate setters
    if (setDateRange) {
      setDateRange(startISODate, endISODate);
    } else {
      setStartDate(startISODate);
      setEndDate(endISODate);
    }
    setIsOpen(false);
  };

  const applyPredefined = (days) => {
    const [startISO, endISO] = predefinedRange(days);
    // Update local display state
    setCustomRange([isoDateToDisplayDate(startISO), isoDateToDisplayDate(endISO)]);
    // Notify parent with ISO strings - use atomic update if available
    if (setDateRange) {
      setDateRange(startISO, endISO);
    } else {
      setStartDate(startISO);
      setEndDate(endISO);
    }
    setIsOpen(false);
  };

  const handleRangeChange = (range) => {
    setCustomRange(range.map((d) => (d ? new Date(d) : null)));
  };

  // Calculate days difference from ISO date strings (inclusive count)
  const startMs = startDate ? isoDateToDisplayDate(startDate).getTime() : 0;
  const endMs = endDate ? isoDateToDisplayDate(endDate).getTime() : 0;
  const daysDiff = Math.round((endMs - startMs) / (24 * 3600 * 1000)) + 1;

  return (
    <Popover opened={isOpen} onChange={setIsOpen} position="bottom-start" shadow="md">
      <Popover.Target>
        <Button variant="default" onClick={() => setIsOpen(!isOpen)} size="sm" leftSection={<IconCalendar size={16} />}>
          {formatISODate(startDate)} - {formatISODate(endDate)} ({daysDiff}d)
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Card padding={0} style={{ border: 'none' }}>
          <Group justify="flex-end" mb="xs">
            <Button variant="subtle" size="xs" onClick={() => setIsOpen(false)}>
              <IconX size={16} />
            </Button>
          </Group>

          {isMobile === false ? (
            <Group align="flex-start" gap="md" wrap="nowrap">
              {preSetRanges && (
                <Stack gap="xs">
                  <Button variant="subtle" size="xs" justify="flex-start" onClick={() => applyPredefined(7)}>
                    1 week
                  </Button>
                  <Button variant="subtle" size="xs" justify="flex-start" onClick={() => applyPredefined(14)}>
                    2 weeks
                  </Button>
                  <Button variant="subtle" size="xs" justify="flex-start" onClick={() => applyPredefined(30)}>
                    1 month
                  </Button>
                  <Button variant="subtle" size="xs" justify="flex-start" onClick={() => applyPredefined(90)}>
                    3 months
                  </Button>
                </Stack>
              )}
              <DatePicker
                type="range"
                value={customRange}
                onChange={handleRangeChange}
                numberOfColumns={numberOfColumns}
                minDate={isoDateToDisplayDate(minDate) || undefined}
                maxDate={isoDateToDisplayDate(maxDate) || isoDateToDisplayDate(todayISODate())}
              />
            </Group>
          ) : (
            <Stack gap="md">
              {preSetRanges && (
                <Group gap="xs" wrap="wrap">
                  <Button variant="subtle" size="xs" onClick={() => applyPredefined(7)}>
                    1 week
                  </Button>
                  <Button variant="subtle" size="xs" onClick={() => applyPredefined(14)}>
                    2 weeks
                  </Button>
                  <Button variant="subtle" size="xs" onClick={() => applyPredefined(30)}>
                    1 month
                  </Button>
                  <Button variant="subtle" size="xs" onClick={() => applyPredefined(90)}>
                    3 months
                  </Button>
                </Group>
              )}
              <DatePicker
                type="range"
                value={customRange}
                onChange={handleRangeChange}
                numberOfColumns={1}
                minDate={isoDateToDisplayDate(minDate) || undefined}
                maxDate={isoDateToDisplayDate(maxDate) || isoDateToDisplayDate(todayISODate())}
              />
            </Stack>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" size="sm" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!customRange[1]} onClick={() => onSetDates(customRange)}>
              Apply
            </Button>
          </Group>
        </Card>
      </Popover.Dropdown>
    </Popover>
  );
};

export default DateRangePicker;
